/* eslint-disable @typescript-eslint/no-unused-vars */
import { Heading, StepProps, Stepper, useSteps } from '@orbita-ui/core';
import { Button, Col, Row, Space, Typography } from 'antd';
import withAuthorization from '../components/hocs/withAuthorization';
import Page from '../components/page';
import { format } from 'date-fns';
import FirstStep from '../features/card-holders/new/first-step';
import SecondStep from '../features/card-holders/new/second-step';
import useCreateCardHolder from '../hooks/data/useCreateCardHolder';
import useToastContext from '../hooks/useToastContext';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { RealmRoles } from '../constants';
import {
  AddressType,
  CardHolderPost,
  CardHolderType,
  CompanyIdentification,
  CountryCode,
  Gender
} from '../interfaces/card-holder';
import { camelCaseObjectToSnakeCase } from '../utils/camelCaseToSnakeCase';
import { useLoading } from '../context/LoadingContext';
import Addresses from 'components/Addresses/addresses';
import Phones, { PhoneFormInputs } from 'components/Phones/phones';
import Identifications from 'components/Identifications/identifications';
import { IdentificationsFormInputs } from 'components/Identifications/types/IdentificationsTypes';
import axios from 'axios';
import { ApiPrepaidCardErrorResponse } from 'interfaces/ApiPrepaidErrors';
import { getPrepaidErrorMessages } from 'utils/errorCodes';

const FormSection = styled.div`
  margin-top: 2rem;
`;

const StepsActionsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
`;

type FirstStepFormInputs = {
  cardHolderType: CardHolderType;
  centralized: string;
  validateIdentification: boolean;
  validateAddress: boolean;
};

type AddressesFormInputs = {
  type: AddressType;
  street: string;
  streetNumber: string;
  city: string;
  state: string;
  country: CountryCode;
  postalCode: string;
  floor: string;
  apt: string;
};


type SecondStepFormInputs = {
  firstName: string;
  lastName: string;
  legalName: string;
  tradeName: string;
  birthDate: string;
  inscriptionDate: string;
  identifications: IdentificationsFormInputs[];
  identificationType: CompanyIdentification;
  nationality: CountryCode;
  gender: string;
  phones: PhoneFormInputs[];
  email: string;
  addresses: AddressesFormInputs[];
  externalId: string;
};

const ThirdStepContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
`;

const genderDictionary = new Map(
  Object.entries({
    MALE: 'Masculino',
    FEMALE: 'Femenino',
    AGENDER: "Sin Genero"
  })
);


const DataDisplay = ({ title, value }) => {
  if (!value) {
    return;
  }
  return (
    <Space>
      <Typography.Text>{`${title}: `}</Typography.Text>
      <Typography.Text type="secondary">{value}</Typography.Text>
    </Space>
  );
};

type ThirdStepProps = {
  firstStepData: FirstStepFormInputs;
  secondStepData: SecondStepFormInputs;
  goToPrevious: () => void;
};

const ThirdStep = ({ goToPrevious, firstStepData, secondStepData }: ThirdStepProps) => {
  const navigate = useNavigate();
  const { issuer } = useParams<{ issuer: string }>();
  const toast = useToastContext();
  const { setLoading, setSuccess, setErrors } = useLoading();

  const {
    createCardHolder,
    isLoading: createCardHolderLoading,
    isError: createCardHolderError,
  } = useCreateCardHolder(firstStepData?.validateIdentification, firstStepData?.validateAddress);

  const handleCardHolderCreation = async () => {
    const payload: CardHolderPost = {
      type: firstStepData.cardHolderType,
      centralized_balance: firstStepData.centralized.toLowerCase() === 'true',
      email: secondStepData.email,
      nationality: secondStepData.nationality,
      identifications: secondStepData.identifications.map((identificacion) =>
        camelCaseObjectToSnakeCase(identificacion)
      ),
      addresses: secondStepData.addresses.map((address) => camelCaseObjectToSnakeCase(address)),
      ...(secondStepData?.phones?.length > 0 && {
        phones: secondStepData?.phones?.map((phone) => camelCaseObjectToSnakeCase(phone)),
      }),
      ...(secondStepData?.firstName && { first_name: secondStepData.firstName }),
      ...(secondStepData?.lastName && { last_name: secondStepData.lastName }),
      ...(secondStepData?.birthDate && { birth_date: format(new Date(secondStepData.birthDate), 'yyyy-MM-dd') }),
      ...(secondStepData?.gender && { gender: secondStepData.gender }),
      ...(secondStepData?.tradeName && { trade_name: secondStepData.tradeName }),
      ...(secondStepData?.legalName && { legal_name: secondStepData.legalName }),
      ...(secondStepData?.inscriptionDate && {
        inscription_date: format(new Date(secondStepData.inscriptionDate), 'yyyy-MM-dd'),
      }),
      ...(secondStepData?.identificationType && { identification_type: secondStepData.identificationType }),
      ...(secondStepData?.externalId && { external_id: secondStepData.externalId }),
    };
    try {
      setLoading(true)
      const createCardHolderResult = await createCardHolder(payload);
      setLoading(false)
      setSuccess({title:"Se ha creado el cardholder satisfactoriamente.", extraButtons: [
          // @ts-ignore
        <Button type="primary" key="cardholder" onClick={() => navigate(`/${issuer}/card-holders/${createCardHolderResult.data?.id}`)}>
          Ver
        </Button>,
        <Button key="continue" onClick={() => navigate(`/${issuer}/card-holders`)}>Continuar</Button>
      ]})
    } catch (error: unknown) {
      setLoading(false)

      if (axios.isAxiosError(error)) {
        // Handle Axios-specific error
        const prepaidcardErrors: ApiPrepaidCardErrorResponse = error.response?.data
        const errorsMessages =  getPrepaidErrorMessages(prepaidcardErrors)

        setErrors(errorsMessages);
      } else if (error instanceof Error) {
        // Handle general JS errors
        console.error('General Error:', error.message);
      } else {
        // Handle unknown errors
        console.error('Unknown error', error);
      }
    }
  };

  return (
    <>
      <Heading bold as="h3">
        Informacion
      </Heading>
      <ThirdStepContainer style={{marginTop:'0.5em'}}>
        <DataDisplay value={secondStepData?.firstName} title="Nombre" />
        <DataDisplay value={secondStepData?.lastName} title="Apellido" />
        <DataDisplay value={secondStepData?.legalName} title="Nombre legal" />
        <DataDisplay value={secondStepData?.tradeName} title="Nombre de fantasia" />
        <DataDisplay
          value={secondStepData?.birthDate ? format(new Date(secondStepData?.birthDate), 'dd/MM/yyyy') : null}
          title="Fecha de nacimiento"
        />
        <DataDisplay
          value={
            secondStepData?.inscriptionDate ? format(new Date(secondStepData?.inscriptionDate), 'dd/MM/yyyy') : null
          }
          title="Fecha de inscripcion"
        />
        <DataDisplay value={secondStepData?.identificationType} title="Tipo de identificacion" />
        <DataDisplay value={secondStepData?.nationality} title="Nacionalidad" />
        <DataDisplay value={genderDictionary.get(secondStepData?.gender)} title="Genero" />
        <DataDisplay value={secondStepData?.email} title="Email" />
        <DataDisplay value={secondStepData?.externalId} title="ID Externo" />
      </ThirdStepContainer>

      <Row gutter={24} style={{marginTop:'1em'}}>
         <Col span={24}>
            <Identifications identifications={secondStepData?.identifications?.map((identification, index) => ({
                ...identification,
                id: index,
            }))} />
         </Col>
      </Row>

      <Row gutter={24} style={{marginTop:'1em'}}>
         <Col span={24}>
            <Addresses addresses={ secondStepData?.addresses?.map((address, index) => ({
                ...address,
                id: index,
            })) }/>
         </Col>
      </Row>

      <Row gutter={24} style={{marginTop:'1em'}}>
         <Col span={24}>
            <Phones phones={secondStepData?.phones?.map((phone, index) => ({
                ...phone,
                id: index,
              }))}/>
         </Col>
      </Row>

      <StepsActionsContainer>
        <Button onClick={() => goToPrevious()}>Anterior</Button>
        <Button onClick={handleCardHolderCreation} type="primary">
          Confirmar
        </Button>
      </StepsActionsContainer>
    </>
  );
};

const CardHoldersNew = () => {
  const [firstStepData, setFirstStepData] = useState<FirstStepFormInputs | null>(null);
  const [secondStepData, setSecondStepData] = useState<SecondStepFormInputs | null>(null);

  const steps: StepProps[] = [
    {
      label: 'Titular',
      icon: 'UserIcon',
    },
    {
      label: 'Datos Personales',
      icon: 'NameTagIcon',
    },
    {
      label: 'Resumen',
      icon: 'AddIcon',
    },
  ];

  const { activeStep, goToPrevious, goToNext } = useSteps({
    count: steps.length,
    index: 0,
  });

  return (
    <Page title="Nuevo titular de tarjeta" >
      <Stepper activeStep={activeStep} steps={steps} />
      {activeStep === 0 && <FirstStep {...{ goToNext, setFirstStepData, setSecondStepData, firstStepData }} />}
      {activeStep === 1 && (
        <SecondStep {...{ goToNext, goToPrevious, setSecondStepData, firstStepData, secondStepData }} />
      )}
      {activeStep === 2 && <ThirdStep {...{ goToPrevious, firstStepData, secondStepData }} />}
    </Page>
  );
};

export default withAuthorization([RealmRoles.CardHolderAllWrite], CardHoldersNew);
